import React, { createContext, useContext, useState, useCallback, ReactNode, useRef, useEffect } from 'react';
import { XCircleIcon } from '@heroicons/react/24/solid';

interface ErrorState {
  id: string;
  message: string;
  timestamp: number;
}

interface ErrorContextType {
  errors: ErrorState[];
  addError: (message: string) => void;
  removeError: (id: string) => void;
  clearErrors: () => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

interface ErrorProviderProps {
  children: ReactNode;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
  const [errors, setErrors] = useState<ErrorState[]>([]);
  const timeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const addError = useCallback((message: string) => {
    const id = `error-${Date.now()}-${Math.random()}`;
    setErrors(prev => [...prev, { id, message, timestamp: Date.now() }]);

    // Auto-remove after 10 seconds
    const timeoutId = setTimeout(() => {
      setErrors(prev => prev.filter(e => e.id !== id));
      timeoutsRef.current.delete(id);
    }, 10000);

    timeoutsRef.current.set(id, timeoutId);
  }, []);

  const removeError = useCallback((id: string) => {
    setErrors(prev => prev.filter(e => e.id !== id));
    const timeoutId = timeoutsRef.current.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutsRef.current.delete(id);
    }
  }, []);

  const clearErrors = useCallback(() => {
    setErrors([]);
    timeoutsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
    timeoutsRef.current.clear();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => {
      timeouts.forEach(timeoutId => clearTimeout(timeoutId));
      timeouts.clear();
    };
  }, []);

  return (
    <ErrorContext.Provider value={{ errors, addError, removeError, clearErrors }}>
      {children}

      {/* Persistent Error Display */}
      {errors.length > 0 && (
        <div className="fixed top-24 right-4 z-[60] space-y-2 max-w-md">
          {errors.map((error) => (
            <div
              key={error.id}
              className="bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg animate-error-slide-in"
            >
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <XCircleIcon className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm text-red-800">{error.message}</p>
                </div>
                <button
                  onClick={() => removeError(error.id)}
                  className="ml-3 inline-flex text-red-400 hover:text-red-600 transition-colors duration-200"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </ErrorContext.Provider>
  );
};
